import { useState, Fragment } from "react";
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DisparoCampaignMessages } from "./DisparoCampaignMessages";
import { DisparoRescheduleDialog } from "./DisparoRescheduleDialog";
import type { DisparoCampaign, DisparoCampaignStatus } from "@/lib/disparos-types";
import { DISPARO_STATUS_LABELS } from "@/lib/disparos-types";
import { cn } from "@/lib/utils";

const STATUS_VARIANT: Record<
  DisparoCampaignStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  draft: "secondary",
  scheduled: "outline",
  running: "default",
  paused: "outline",
  suspended: "destructive",
  completed: "secondary",
  failed: "destructive",
  cancelled: "destructive",
};

interface DisparoCampaignsTabProps {
  workspaceId: string;
  campaigns: DisparoCampaign[];
  loading: boolean;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
  onReschedule: (id: string, values: { integration_id: string; scheduled_at: string }) => void;
  onCleanup: () => void;
  controlling: boolean;
  rescheduling: boolean;
}

export function DisparoCampaignsTab({
  workspaceId,
  campaigns,
  loading,
  onPause,
  onResume,
  onDelete,
  onReschedule,
  onCleanup,
  controlling,
  rescheduling,
}: DisparoCampaignsTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const rescheduleCampaign = campaigns.find((c) => c.id === rescheduleId) ?? null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Carregando campanhas...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onCleanup}>
          Opções de limpeza
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">
          Nenhuma campanha de disparo ainda.
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Campanha</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Enviadas</TableHead>
                <TableHead className="text-right">Falhas</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((campaign) => {
                const expanded = expandedId === campaign.id;
                const canPause = campaign.status === "running";
                const canResume = campaign.status === "paused";
                const isSuspended = campaign.status === "suspended";

                return (
                  <Fragment key={campaign.id}>
                    <TableRow>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setExpandedId(expanded ? null : campaign.id)}
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        {campaign.name}
                        {isSuspended && (
                          <span className="block text-xs font-normal text-destructive">
                            Conexão desconectada — reagende para continuar
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[campaign.status]}>
                          {DISPARO_STATUS_LABELS[campaign.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{campaign.sent}</TableCell>
                      <TableCell className="text-right tabular-nums text-destructive">
                        {campaign.failed}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{campaign.total}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {format(new Date(campaign.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={controlling}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canPause && !isSuspended && (
                              <DropdownMenuItem onClick={() => onPause(campaign.id)}>
                                <Pause className="w-4 h-4 mr-2" />
                                Pausar
                              </DropdownMenuItem>
                            )}
                            {isSuspended && (
                              <DropdownMenuItem onClick={() => setRescheduleId(campaign.id)}>
                                <CalendarClock className="w-4 h-4 mr-2" />
                                Reagendar
                              </DropdownMenuItem>
                            )}
                            {canResume && !isSuspended && (
                              <DropdownMenuItem onClick={() => onResume(campaign.id)}>
                                <Play className="w-4 h-4 mr-2" />
                                Retomar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteId(campaign.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow key={`${campaign.id}-messages`}>
                        <TableCell colSpan={8} className={cn("bg-muted/30 p-4")}>
                          <DisparoCampaignMessages
                            workspaceId={workspaceId}
                            campaignId={campaign.id}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DisparoRescheduleDialog
        open={!!rescheduleId}
        onOpenChange={(o) => !o && setRescheduleId(null)}
        workspaceId={workspaceId}
        campaignName={rescheduleCampaign?.name ?? ""}
        loading={rescheduling}
        onConfirm={(values) => {
          if (rescheduleId) onReschedule(rescheduleId, values);
          setRescheduleId(null);
        }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir campanha?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Mensagens pendentes serão canceladas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) onDelete(deleteId);
                setDeleteId(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
